// @ts-check
/**
 * React Router v6 Data Router loaders.
 *
 * Each loader runs BEFORE the route component mounts, so the server data is
 * ready on first render and there is no skeleton flash. Consumers read it with
 * `useLoaderData()`.
 *
 * Guidelines:
 *   - Always forward `request.signal` to axios so React Router can cancel the
 *     in-flight request when the user bounces to another route mid-navigation.
 *   - Swallow 401 quietly — ProtectedRoute handles the redirect.
 *   - Return the raw response shape the view already expects; the view can
 *     still refetch as normal for subsequent refreshes.
 *   - Path arguments to `typedApi.get` are checked against the OpenAPI schema
 *     in `src/api/generated.ts`. Renaming or removing a backend endpoint will
 *     fail the type-check here.
 */
import { typedApi } from './typed';
import { startOfWeek, addDays, format } from 'date-fns';
import { logger } from '../utils/logger';

/**
 * @typedef {import('react-router-dom').LoaderFunctionArgs} LoaderArgs
 */

/**
 * @template T
 * @param {() => Promise<T>} fn
 * @param {string} label For debugging.
 * @returns {Promise<T | null>}
 */
async function safe(fn, label) {
  try {
    return await fn();
  } catch (err) {
    /** @type {any} */
    const e = err;
    if (e && (e.name === 'CanceledError' || e.name === 'AbortError')) throw e;
    if (e && e.response && e.response.status === 401) return null;
    logger.error(`Loader fetch failed (${label}):`, e);
    return null;
  }
}

/** @param {LoaderArgs} args */
export async function commitmentsLoader({ request }) {
  const url = new URL(request.url);
  const status = url.searchParams.get('status');
  const params = status ? { status } : {};
  const data = await safe(
    () => typedApi.get('/api/commitments', { params, signal: request.signal }),
    '/commitments',
  );
  return {
    commitments: (data && data.commitments) || [],
    counts: (data && data.counts) || {},
  };
}

// ── Analytics ──
/** @param {LoaderArgs} args */
export async function analyticsLoader({ request }) {
  const [overview, trends] = await Promise.all([
    safe(() => typedApi.get('/api/analytics/overview', { signal: request.signal }), '/analytics/overview'),
    safe(() => typedApi.get('/api/analytics/trends', { params: { days: 7 }, signal: request.signal }), '/analytics/trends'),
  ]);
  return { overview, trends };
}

// ── Inbox ──
/** @param {LoaderArgs} args */
export async function inboxLoader({ request }) {
  // We intentionally skip the businessId filter: activeBusiness lives in
  // React context and isn't available in loaders. The view re-fetches with
  // the filter once it mounts; this loader delivers the instant first paint.
  const data = await safe(
    () => typedApi.get('/api/inbox', { signal: request.signal }),
    '/inbox',
  );
  return { items: Array.isArray(data) ? data : [] };
}

// ── Payments ──
/** @param {LoaderArgs} args */
export async function paymentsLoader({ request }) {
  const [payments, summary] = await Promise.all([
    safe(() => typedApi.get('/api/payments', { signal: request.signal }), '/payments'),
    safe(() => typedApi.get('/api/payments/summary', { signal: request.signal }), '/payments/summary'),
  ]);
  return {
    payments: Array.isArray(payments) ? payments : [],
    summary,
  };
}

// ── Today ──
/** @param {LoaderArgs} args */
export async function todayLoader({ request }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [decisions, blocks, scheduledTasks, todayTasks, widgets] = await Promise.all([
    safe(() => typedApi.get('/api/decisions/queue', { signal: request.signal }), '/decisions/queue'),
    safe(() => typedApi.get('/api/timeblocks', { params: { date: today }, signal: request.signal }), '/timeblocks'),
    safe(() => typedApi.get('/api/tasks', { params: { scheduledDate: today }, signal: request.signal }), '/tasks?scheduled'),
    safe(() => typedApi.get('/api/tasks', { params: { status: 'TODAY' }, signal: request.signal }), '/tasks?today'),
    // Secondary widgets super-bundle: returns velocity + stakeholder-heat +
    // attention-budget in one round trip. Used to prime the three child
    // widget caches in TodayView via `useTodayWidgetsBundle`.
    safe(() => typedApi.get('/api/today/widgets', { signal: request.signal }), '/today/widgets'),
  ]);
  const all = [
    ...(Array.isArray(scheduledTasks) ? scheduledTasks : []),
    ...(Array.isArray(todayTasks) ? todayTasks : []),
  ];
  const deduped = Array.from(new Map(all.map((t) => [t.id, t])).values());
  return {
    decisions: decisions || {},
    blocks: Array.isArray(blocks) ? blocks : [],
    tasks: deduped,
    widgets: widgets || null,
    today,
  };
}

// ── Calendar ──
/** @param {LoaderArgs} args */
export async function calendarLoader({ request }) {
  // We pre-load the "current week" view that Calendar opens on. Week is
  // resolved client-side anyway; this is a best-effort hydration.
  const today = new Date();
  const ws = startOfWeek(today, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const startStr = format(days[0], 'yyyy-MM-dd');
  const endStr = format(days[6], 'yyyy-MM-dd');

  const [tasks, courtDays, ...blockArrays] = await Promise.all([
    safe(() => typedApi.get('/api/tasks', { signal: request.signal }), '/tasks'),
    safe(() => typedApi.get('/api/court-days', { params: { start: startStr, end: endStr }, signal: request.signal }), '/court-days'),
    ...days.map((d) =>
      safe(
        () => typedApi.get('/api/timeblocks', { params: { date: format(d, 'yyyy-MM-dd') }, signal: request.signal }),
        '/timeblocks',
      ),
    ),
  ]);

  return {
    tasks: tasks || [],
    courtDays: courtDays || { courtDays: [] },
    blocks: (blockArrays || []).flatMap((arr) => arr || []),
    weekStart: startStr,
  };
}
